import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Paperclip, Download, Trash2, FileText, Image as ImageIcon, File } from "lucide-react";
import { Avatar } from "./BoardCells";
import { Spinner } from "../ui";
import { timeAgo, personOf, fmtBytes } from "./social";

const iconFor = (ct) => {
  if (!ct) return File;
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.includes("pdf") || ct.startsWith("text/")) return FileText;
  return File;
};

export default function FilesTab({ itemId, me }) {
  const { t, toast } = useApp();
  const { lang } = useApp();
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/board-items/${itemId}/files`);
    setFiles(data);
  }, [itemId]);
  useEffect(() => { setFiles(null); load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post(`/board-items/${itemId}/files`, fd); await load(); toast(t("files_uploaded")); }
    catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (d && typeof d === "object") ? d.message : (d || t("files_error"));
      toast(msg, { variant: "error" });
    }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const open = async (f) => {
    try {
      const res = await api.get(`/board-files/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename || "file"; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast(t("files_error"), { variant: "error" }); }
  };
  const del = async (f) => {
    try { await api.delete(`/board-files/${f.id}`); await load(); toast(t("files_deleted")); }
    catch { toast(t("files_error"), { variant: "error" }); }
  };

  return (
    <div className="space-y-3" data-testid="files-tab">
      <input ref={inputRef} type="file" className="hidden" onChange={upload} data-testid="files-input" />
      <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="files-upload-btn"
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed text-sm font-medium text-text-secondary hover:border-primary/50 hover:text-text-primary transition-colors">
        {busy ? <Spinner className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}{t("files_add")}
      </button>
      {files === null ? (
        <div className="py-8 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>
      ) : files.length === 0 ? (
        <div className="py-8 text-center text-sm text-text-secondary"><Paperclip className="w-7 h-7 mx-auto mb-2 opacity-40" />{t("files_empty")}</div>
      ) : (
        <div className="space-y-1.5">
          {files.map((f) => {
            const Icon = iconFor(f.content_type);
            const mine = f.actor_id === me;
            return (
              <div key={f.id} data-testid={`file-${f.id}`} className="group/fl flex items-center gap-3 px-3 py-2.5 rounded-xl border hover:border-primary/40 hover:bg-surface-hover transition-colors">
                <span className="w-9 h-9 rounded-lg bg-surface flex items-center justify-center text-text-secondary shrink-0"><Icon className="w-4.5 h-4.5" /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{f.filename}</p>
                  <p className="text-[11px] text-text-secondary flex items-center gap-1.5">
                    <Avatar person={personOf(f)} size={14} /> {f.actor_name} · {fmtBytes(f.size)} · {timeAgo(f.created_at, lang)}
                  </p>
                </div>
                <button onClick={() => open(f)} data-testid={`file-download-${f.id}`} title={t("files_download")} className="text-text-secondary hover:text-primary transition-colors shrink-0"><Download className="w-4 h-4" /></button>
                {mine && <button onClick={() => del(f)} data-testid={`file-delete-${f.id}`} className="opacity-0 group-hover/fl:opacity-100 text-text-secondary hover:text-red-500 transition-all shrink-0"><Trash2 className="w-4 h-4" /></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
